import './style.css';
import {
  initMap,
  setActiveCategories,
  addMarkersFromGeoJSON,
  getCategoryColor,
  setFollowCamera,
  watchMarkers,
} from './map.js';
import { db, CATEGORIES } from './db.js';
import { RideController } from './ride.js';
import { fetchNearbyPOIs } from './poi.js';
import { watchMapPOIs } from './poi.js';
import { OSM_CATEGORIES, OSM_CATEGORY_KEYS } from './categories.js';
import { distance } from '@turf/turf';
import { routeToPlace, searchPlaces } from './search.js';
import { NavigationController } from './nav.js';

const els = {
  netBadge: document.getElementById('net-badge'),
  netText: document.getElementById('net-text'),
  storageGauge: document.getElementById('storage-gauge'),
  storageBar: document.getElementById('storage-bar'),
  storageText: document.getElementById('storage-text'),
  menuBtn: document.getElementById('menu-btn'),
  drawerClose: document.getElementById('drawer-close'),
  drawerOverlay: document.getElementById('drawer-overlay'),
  sidePanel: document.getElementById('side-panel'),
  filterList: document.getElementById('filter-list'),
  importMarkersBtn: document.getElementById('import-markers-btn'),
  markersInput: document.getElementById('markers-input'),
  seedBtn: document.getElementById('seed-btn'),
  markerCount: document.getElementById('marker-count'),
  toast: document.getElementById('toast'),
  turnCard: document.getElementById('turn-card'),
  turnIcon: document.getElementById('turn-icon'),
  turnText: document.getElementById('turn-text'),
  turnStreet: document.getElementById('turn-street'),
  turnDist: document.getElementById('turn-dist'),
  fabSearch: document.getElementById('fab-search'),
  fabPoi: document.getElementById('fab-poi'),
  fabLoc: document.getElementById('fab-loc'),
  dashSpeed: document.getElementById('dash-speed'),
  dashDist: document.getElementById('dash-dist'),
  dashEta: document.getElementById('dash-eta'),
  dashTime: document.getElementById('dash-time'),
  dashFollow: document.getElementById('dash-follow'),
  speedRingFill: document.getElementById('speed-ring-fill'),
  searchForm: document.getElementById('search-form'),
  searchInput: document.getElementById('search-input'),
  searchButton: document.getElementById('search-button'),
  searchResults: document.getElementById('search-results'),
  nearbyList: document.getElementById('nearby-list'),
  osmFilterList: document.getElementById('osm-filter-list'),
  navOverlay: document.getElementById('nav-overlay'),
  navExit: document.getElementById('nav-exit'),
  navRecenter: document.getElementById('nav-recenter'),
  navSpeed: document.getElementById('nav-speed'),
  navSpeedBottom: document.getElementById('nav-speed-bottom'),
  navSpeedRingFill: document.getElementById('nav-speed-ring-fill'),
  navDist: document.getElementById('nav-dist'),
  navEta: document.getElementById('nav-eta'),
  navTime: document.getElementById('nav-time'),
  navProgressBar: document.getElementById('nav-progress-bar'),
  arrivalOverlay: document.getElementById('arrival-overlay'),
  arrivalDismiss: document.getElementById('arrival-dismiss'),
  tripDist: document.getElementById('trip-dist'),
  tripDuration: document.getElementById('trip-duration'),
};

let drawerOpen = false;
let rideController;
let navController;
let lastRoute = null;
let navStartTime = null;
let lastPOIFetch = 0;
let lastPOICenter = null;
let activeOSMCategories = new Set(OSM_CATEGORY_KEYS);
let stopPOIWatcher = null;

function init() {
  registerSW();
  initNetworkBadge();
  initStorageGauge();
  initDrawer();
  initFilters();
  initOSMFilters();
  initMarkerImporter();
  initRideControls();
  initNavigation();
  initSearch();
  initNearbyList();
  updateMarkerCount();
  loadReadyMap();
}

function initDrawer() {
  const open = () => {
    drawerOpen = true;
    document.body.classList.add('drawer-open');
    els.drawerOverlay.removeAttribute('hidden');
  };
  const close = () => {
    drawerOpen = false;
    document.body.classList.remove('drawer-open');
    els.drawerOverlay.setAttribute('hidden', '');
  };
  els.menuBtn.addEventListener('click', open);
  els.drawerClose.addEventListener('click', close);
  els.drawerOverlay.addEventListener('click', close);
}

function initOSMFilters() {
  els.osmFilterList.innerHTML = '';
  for (const key of OSM_CATEGORY_KEYS) {
    const config = OSM_CATEGORIES[key];
    const label = document.createElement('label');
    label.innerHTML = `<input type="checkbox" value="${key}" checked><span class="swatch" style="background:${config.color}"></span><span>${config.label}</span>`;
    els.osmFilterList.appendChild(label);
  }
  els.osmFilterList.addEventListener('change', () => {
    activeOSMCategories = new Set(Array.from(els.osmFilterList.querySelectorAll('input:checked')).map((input) => input.value));
    stopPOIWatcher?.();
    stopPOIWatcher = watchMapPOIs(() => activeOSMCategories, showToast);
  });
}

function initRideControls() {
  rideController = new RideController(updateRideHUD, showToast);
  els.dashFollow.addEventListener('click', () => {
    rideController.setFollowMode(!rideController.followMode);
  });
  els.fabLoc.addEventListener('click', () => {
    if (!rideController.lastPosition) {
      // No fix yet — (re)start GPS acquisition on user gesture.
      return startGps();
    }
    const { lng, lat, heading } = rideController.lastPosition;
    setFollowCamera(true, lng, lat, heading || 0, 17);
  });
  els.fabPoi.addEventListener('click', async () => {
    const position = rideController.lastPosition;
    if (!position) return startGps();
    await fetchNearbyPOIs(position.lat, position.lng, showToast, activeOSMCategories);
    updateNearbyList();
  });
  els.fabSearch.addEventListener('click', () => {
    els.searchInput.focus();
  });
  // Tap the GPS pill to start / retry acquisition (critical when permission
  // was denied or the cold fix timed out).
  els.gpsPill.addEventListener('click', () => startGps());
  els.gpsPill.style.cursor = 'pointer';
}

async function startGps() {
  if (!('geolocation' in navigator)) {
    return showToast('Geolocation is not supported by this browser.');
  }
  // Surface permission state so the user knows when they must re-enable it.
  if (navigator.permissions?.query) {
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      if (status.state === 'denied') {
        showToast('Location is blocked. Enable it in browser site settings, then tap GPS again.');
      } else if (status.state === 'prompt') {
        showToast('Allow location access when prompted.');
      } else {
        showToast('Acquiring GPS…');
      }
    } catch { /* permissions API optional */ }
  }
  try {
    await rideController.track();
  } catch (err) {
    console.warn('[GPS] start failed', err.message);
    showToast(`GPS unavailable: ${err.message}`);
  }
}

function initNavigation() {
  navController = new NavigationController(updateNavHUD, onArrival, onOffRoute);
  els.navExit.addEventListener('click', stopNavigation);
  els.navRecenter.addEventListener('click', () => {
    if (!rideController?.lastPosition) return;
    const { lng, lat, heading } = rideController.lastPosition;
    setFollowCamera(true, lng, lat, heading || 0, 17);
  });
  els.arrivalDismiss.addEventListener('click', () => {
    els.arrivalOverlay.setAttribute('hidden', '');
  });
}

function startNavigation() {
  if (!lastRoute) return showToast('Search a destination first.');
  if (!rideController?.lastPosition) return showToast('Waiting for GPS lock.');
  if (navController.start(lastRoute)) {
    els.navOverlay.removeAttribute('hidden');
    els.turnCard.removeAttribute('hidden');
    rideController.setFollowMode(true);
    navStartTime = Date.now();
    showToast('Navigation started');
  }
}

function stopNavigation() {
  navController.stop();
  els.navOverlay.setAttribute('hidden', '');
  els.turnCard.setAttribute('hidden', '');
  showToast('Navigation stopped');
}

function onArrival() {
  stopNavigation();
  els.tripDist.textContent = lastRoute ? `${(lastRoute.distance / 1000).toFixed(1)} km` : '--';
  els.tripDuration.textContent = navStartTime ? formatDuration(Date.now() - navStartTime) : '--';
  els.arrivalOverlay.removeAttribute('hidden');
  showToast('You arrived!');
}

function onOffRoute(dist) {
  showToast(`Off route by ${Math.round(dist)}m — recalculating…`);
  if (lastRoute && rideController?.lastPosition) {
    recalcRoute();
  }
}

async function recalcRoute() {
  if (!lastRoute || !rideController?.lastPosition) return;
  try {
    const origin = [rideController.lastPosition.lng, rideController.lastPosition.lat];
    const dest = lastRoute.geometry.coordinates[lastRoute.geometry.coordinates.length - 1];
    const result = await routeToPlace({ lon: dest[0], lat: dest[1], display_name: 'Destination' }, origin);
    lastRoute = result.route;
    navController.start(lastRoute);
  } catch (err) {
    console.warn('[Nav] recalc failed', err);
  }
}

function initSearch() {
  els.searchForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const query = els.searchInput.value.trim();
    if (!query) return;
    els.searchButton.disabled = true;
    els.searchResults.classList.remove('hidden');
    els.searchResults.innerHTML = '<div class="list-empty">Searching…</div>';
    try {
      const results = await searchPlaces(query);
      renderList(els.searchResults, results, 'No places or markers found.');
    } catch (error) {
      renderList(els.searchResults, [], error.message);
    } finally {
      els.searchButton.disabled = false;
    }
  });
}

function renderList(container, items, emptyText) {
  container.innerHTML = '';
  if (!items.length) {
    container.innerHTML = `<div class="list-empty">${emptyText}</div>`;
    return;
  }
  items.forEach((place) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'list-item';
    const ico = place.type === 'place' ? '🌍' : place.type === 'poi' ? '📍' : '🗂';
    const meta = place.type === 'place'
      ? 'OpenStreetMap'
      : place.type === 'poi'
      ? (place.osmCategory || place.category)
      : place.category;
    button.innerHTML = `
      <div class="list-item-ico">${ico}</div>
      <div class="list-item-info">
        <div class="list-item-name">${escapeHtml(place.display_name)}</div>
        <div class="list-item-meta">${meta}${place.address ? ' · ' + escapeHtml(place.address) : ''}</div>
      </div>
    `;
    button.addEventListener('click', async () => {
      try {
        const origin = rideController.lastPosition ? [rideController.lastPosition.lng, rideController.lastPosition.lat] : null;
        const result = await routeToPlace({ lon: place.lng, lat: place.lat, display_name: place.display_name }, origin);
        lastRoute = result.route;
        startNavigation();
        let msg = 'Route ready';
        if (result.cached) msg = 'Route loaded from cache (offline)';
        else if (result.fallback) msg = 'Straight-line route (offline)';
        else if (result.route.distance != null) msg = `Route ready: ${(result.route.distance / 1000).toFixed(1)} km · ${(result.route.duration / 60).toFixed(0)} min`;
        showToast(msg);
      } catch (error) {
        showToast(error.message);
      }
    });
    container.appendChild(button);
  });
}

async function initNearbyList() {
  await updateNearbyList();
}

async function updateNearbyList() {
  try {
    const position = rideController?.lastPosition;
    if (!position) {
      els.nearbyList.innerHTML = '<div class="list-empty">Waiting for GPS lock…</div>';
      return;
    }
    const [markers, pois] = await Promise.all([
      db.getInBounds(getNearbyBbox(position)),
      db.getPOIsInBounds(getNearbyBbox(position), activeOSMCategories),
    ]);
    const items = [
      ...markers.map((m) => ({ type: 'marker', ...m, display_name: m.name || m.category })),
      ...pois.map((p) => ({ type: 'poi', ...p, display_name: p.name || p.category })),
    ].slice(0, 20);
    renderList(els.nearbyList, items, 'No nearby markers.');
  } catch (err) {
    console.error('[Nearby] failed', err);
    els.nearbyList.innerHTML = '<div class="list-empty">Unable to load nearby markers.</div>';
  }
}

function getNearbyBbox(position, radiusM = 2000) {
  const deltaLat = radiusM / 111320;
  const deltaLng = radiusM / (111320 * Math.max(0.2, Math.cos((position.lat * Math.PI) / 180)));
  return [position.lng - deltaLng, position.lat - deltaLat, position.lng + deltaLng, position.lat + deltaLat];
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function updateRideHUD(snapshot) {
  const speed = Number(snapshot.speedKmh || 0);
  els.dashSpeed.textContent = speed.toFixed(0);
  updateSpeedRing(els.speedRingFill, speed);
  els.dashFollow.textContent = snapshot.followMode ? 'FOLLOW' : 'FREE';
  els.dashFollow.classList.toggle('inactive', !snapshot.followMode);

  const gpsState = snapshot.state;
  els.gpsPill.classList.toggle('ok', gpsState === 'tracking' && !snapshot.lastError);
  els.gpsPill.classList.toggle('gps-error', gpsState === 'error' || (gpsState === 'tracking' && !!snapshot.lastError));
  els.gpsPill.classList.toggle('gps-acquiring', gpsState === 'acquiring');
  if (gpsState === 'error') {
    els.gpsText.textContent = 'GPS denied';
  } else if (gpsState === 'acquiring') {
    els.gpsText.textContent = 'GPS searching…';
  } else if (gpsState === 'tracking' && snapshot.lastError) {
    els.gpsText.textContent = 'GPS weak';
  } else if (gpsState === 'tracking') {
    const acc = snapshot.accuracy ? ` ±${Math.round(snapshot.accuracy)}m` : '';
    els.gpsText.textContent = `GPS live${acc}`;
  } else {
    els.gpsText.textContent = 'GPS…';
  }

  if (navController.active) {
    navController.update(snapshot.position);
  }
  autoFetchRealPOIs(snapshot);
}

function updateNavHUD(data) {
  els.turnIcon.textContent = data.turnIcon;
  els.turnText.textContent = data.turnText;
  els.turnStreet.textContent = data.turnStreet || '--';
  els.turnDist.textContent = data.distToTurn || '--';
  els.navSpeed.textContent = Math.round(data.speedKmh);
  els.navSpeedBottom.textContent = Math.round(data.speedKmh);
  updateSpeedRing(els.navSpeedRingFill, data.speedKmh);
  els.navDist.textContent = data.remainingKm < 1 ? `${Math.round(data.remainingM)}m` : `${data.remainingKm.toFixed(1)}km`;
  els.navEta.textContent = data.eta;
  els.navTime.textContent = data.ridingTime;
  els.navProgressBar.style.width = `${data.progress}%`;

  els.dashDist.textContent = data.remainingKm < 1 ? `${Math.round(data.remainingM)}` : data.remainingKm.toFixed(1);
  els.dashEta.textContent = data.eta;
  els.dashTime.textContent = data.ridingTime;
}

function updateSpeedRing(el, speedKmh) {
  const maxDash = 327;
  const pct = Math.min(60, speedKmh) / 60;
  el.style.strokeDashoffset = maxDash * (1 - pct);
}

async function autoFetchRealPOIs(snapshot) {
  const position = snapshot.position;
  if (!position || !navigator.onLine || snapshot.state === 'stopped') return;
  const now = Date.now();
  const movedMeters = lastPOICenter
    ? distance([lastPOICenter.lng, lastPOICenter.lat], [position.lng, position.lat], { units: 'meters' })
    : Infinity;
  if (now - lastPOIFetch < 120000 && movedMeters < 1000) return;
  lastPOIFetch = now;
  lastPOICenter = { lat: position.lat, lng: position.lng };
  await fetchNearbyPOIs(position.lat, position.lng, (message) => {
    if (!message.startsWith('Fetching')) showToast(message);
  }, activeOSMCategories);
  updateNearbyList();
}

async function registerSW() {
  if (!('serviceWorker' in navigator)) {
    showToast('Service workers not supported; offline mode unavailable.');
    return;
  }
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    reg.addEventListener('updatefound', () => {
      const next = reg.installing;
      next?.addEventListener('statechange', () => {
        if (next.state === 'activated') showToast('App updated. Refresh to use latest version.');
      });
    });
    console.log('[SW] registered', reg.scope);
  } catch (err) {
    console.error('[SW] registration failed', err);
    showToast('Service worker registration failed.');
  }
}

function initNetworkBadge() {
  const update = () => {
    const online = navigator.onLine;
    els.netText.textContent = online ? 'Online' : 'Offline';
    els.netBadge.classList.toggle('ok', online);
    els.netBadge.classList.remove('hidden');
  };
  update();
  window.addEventListener('online', update);
  window.addEventListener('offline', update);
}

function initStorageGauge() {
  const update = async () => {
    try {
      const est = await navigator.storage.estimate();
      const used = est.usage || 0;
      const quota = est.quota || 1;
      const pct = Math.min(100, Math.round((used / quota) * 100));
      els.storageBar.style.width = `${pct}%`;
      els.storageText.textContent = `${fmtMB(used)} / ${fmtMB(quota)} (${pct}%)`;
    } catch (err) {
      console.error('[Storage] estimate failed', err);
    }
  };
  update();
  setInterval(update, 30000);
}

function fmtMB(bytes) {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

async function loadReadyMap() {
  try {
    await initMap('map', null);
    watchMarkers(showToast);
    stopPOIWatcher = watchMapPOIs(() => activeOSMCategories, showToast);
    await requestPersistence();
    await autoStartTracking();
  } catch (err) {
    console.warn('[Map] unavailable', err.message);
    showToast(`Map load failed: ${err.message}`);
  }
}

async function autoStartTracking() {
  await startGps();
}

async function requestPersistence() {
  try {
    const persisted = await navigator.storage.persist();
    if (!persisted) showToast('Persistent storage was not granted; offline POI cache may be evicted.');
  } catch (err) {
    console.error('[Storage] persist request failed', err);
    showToast('Persistent storage request failed; offline POI cache may be evicted.');
  }
}

function initFilters() {
  els.filterList.innerHTML = '';
  CATEGORIES.forEach((cat) => {
    const id = `filter-${cat}`;
    const label = document.createElement('label');
    label.innerHTML = `<input type="checkbox" id="${id}" value="${cat}" checked><span class="swatch" style="background:${getCategoryColor(cat)}"></span><span>${cat}</span>`;
    els.filterList.appendChild(label);
  });
  els.filterList.addEventListener('change', () => {
    const checked = Array.from(els.filterList.querySelectorAll('input:checked')).map((i) => i.value);
    setActiveCategories(checked);
  });
}

function initMarkerImporter() {
  els.importMarkersBtn.addEventListener('click', () => els.markersInput.click());
  els.markersInput.addEventListener('change', async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const geojson = JSON.parse(text);
      const result = await addMarkersFromGeoJSON(geojson);
      showToast(`Imported ${result.count.toLocaleString()} markers${result.trimmed ? ' (first 10k)' : ''}`);
      await updateMarkerCount();
      updateNearbyList();
    } catch (err) {
      console.error('[Markers] import failed', err);
      showToast(`Marker import failed: ${err.message}`);
    }
  });
}

async function updateMarkerCount() {
  const count = await db.countMarkers();
  els.markerCount.textContent = `${count.toLocaleString()} markers stored`;
}

let toastTimeout;
export function showToast(message) {
  clearTimeout(toastTimeout);
  els.toast.textContent = message;
  els.toast.removeAttribute('hidden');
  toastTimeout = setTimeout(() => els.toast.setAttribute('hidden', ''), 5000);
}

function formatDuration(ms) {
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}

init();
